import classNames from 'classnames';
import { useState, useRef, useEffect } from 'react';
import { useIntersection } from 'react-use';
import { BaseCheckbox } from 'shared/ui/Checkbox';
import type { Option } from 'shared/ui/MultiSelect/interfaces';
import { getMenuBaseProps } from 'shared/ui/MultiSelect/Multiselect/utils';

import { ListOptionComponent } from './ListOption';
import { ListMenu } from './Menu';
import style from './Search.module.scss';
import { SelectAll } from './SelectAll';

interface MenuSearchProps {
  open: boolean;
  options: Option[];
  selectedOptions: Option[];
  handleSelect: (value: Option[]) => void;
  className?: string;
  isSelectAll?: boolean;
  withoutCheckbox?: boolean;
  singleSelect?: boolean;
  handleClose?: () => void;
  withResize?: boolean;
  menuDefaultWidth?: number;
}

export function MenuSearchComponent({
  options,
  open,
  selectedOptions,
  handleSelect,
  className,
  isSelectAll = true,
  withoutCheckbox,
  singleSelect,
  handleClose,
  withResize,
  menuDefaultWidth,
}: MenuSearchProps) {
  const [search, setSearch] = useState('');
  const scrollAnchor = useRef<HTMLDivElement>(null);

  const intersection = useIntersection(scrollAnchor, {
    root: null,
    rootMargin: '0px',
    threshold: 1,
  });

  const showBorder = intersection && intersection.intersectionRatio < 1;
  const optionsToRender = options.filter((option) => option.text.toLowerCase().includes(search.trim().toLowerCase()));
  const hasResults = optionsToRender.length > 0;

  const handleMultipleSelect = (option: Option) => {
    const selected = selectedOptions.some((selectedOption) => selectedOption.id === option.id);

    if (selected) {
      handleSelect(selectedOptions.filter(({ id }) => id !== option.id));
    } else {
      handleSelect([...selectedOptions, option]);
    }
  };

  useEffect(() => {
    if (open) {
      setSearch('');
    }
  }, [open]);

  const searchInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    searchInput.current?.focus();
  }, [searchInput.current]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };

  const handleSingleSelect = (option: Option) => {
    handleSelect([option]);
    handleClose && handleClose();
  };

  return (
    <ListMenu
      open={open}
      className={className}
      listboxClassName={style.listBoxStyles}
      withResize={withResize}
      menuDefaultWidth={menuDefaultWidth}
      inputBlock={
        <div className={classNames('bg-white py-2 px-4 rounded-t-sm', showBorder && 'border-b-slate-100 border-b')}>
          <input
            className={style.search}
            type="text"
            placeholder="Search"
            value={search}
            onChange={handleSearch}
            onKeyDownCapture={(e) => {
              if (e.key === ' ') {
                e.stopPropagation();
              }
            }}
            ref={searchInput}
          />
        </div>
      }
    >
      <div ref={scrollAnchor} />
      {hasResults && isSelectAll && (
        <SelectAll
          handleSelect={handleSelect}
          selectedOptions={selectedOptions}
          options={optionsToRender}
          withoutCheckbox={withoutCheckbox}
        />
      )}
      {optionsToRender.map((option) => {
        const { key, selected } = getMenuBaseProps({ selectedOptions, option });

        return (
          <ListOptionComponent
            selected={selected}
            key={key}
            handleSelect={singleSelect ? handleSingleSelect : handleMultipleSelect}
            option={option}
            className="py-[10px]"
            renderOption={(selected) => (withoutCheckbox ? <></> : <BaseCheckbox isChecked={selected} />)}
          />
        );
      })}
      {!hasResults && <span className="pl-4 text-sm font-medium text-slate-600">No results</span>}
    </ListMenu>
  );
}
